import { Router } from '@angular/router';
import { Observable, map, of, take } from 'rxjs';
import { StaffUserApiService } from 'src/app/core/services/staff-user-api.service';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isAdminUserUuid(value: string | null | undefined): boolean {
  return UUID_RE.test((value ?? '').trim());
}

export function resolveRowUuid(row: { uuid?: string | null } | null | undefined): string | null {
  const raw = row?.uuid;
  const uuid = typeof raw === 'string' ? raw.trim() : '';
  return isAdminUserUuid(uuid) ? uuid : null;
}

export function adminUserDetailPath(row: { uuid?: string | null }): string[] | null {
  const uuid = resolveRowUuid(row);
  return uuid ? ['/manage-admin-users', uuid] : null;
}

export function adminUserEditPath(row: { uuid?: string | null }): string[] | null {
  const detail = adminUserDetailPath(row);
  return detail ? [...detail, 'edit'] : null;
}

export type AdminUserRouteRow = { id?: number | string | null; uuid?: string | null };

export function resolveAdminUserUuid$(
  userApi: StaffUserApiService,
  row: AdminUserRouteRow,
): Observable<string | null> {
  const direct = resolveRowUuid(row);
  if (direct) {
    return of(direct);
  }
  const id = row?.id;
  if (id == null || id === '') {
    return of(null);
  }
  return userApi.getUserById(+id).pipe(
    map((res) => {
      if (!res?.status || !res?.data) {
        return null;
      }
      return resolveRowUuid(res.data);
    }),
  );
}

export function goToAdminUserDetail(
  router: Router,
  userApi: StaffUserApiService,
  row: AdminUserRouteRow,
  onError?: (message: string) => void,
): void {
  resolveAdminUserUuid$(userApi, row)
    .pipe(take(1))
    .subscribe({
      next: (uuid) => {
        if (uuid) {
          if (row && typeof row === 'object') {
            (row as { uuid?: string }).uuid = uuid;
          }
          void router.navigate(['/manage-admin-users', uuid]);
          return;
        }
        onError?.('Could not open user details. Refresh the list and try again.');
      },
      error: () => {
        onError?.('Could not open user details. Refresh the list and try again.');
      },
    });
}

export function navigateAdminUserEdit(
  router: Router,
  userApi: StaffUserApiService,
  row: AdminUserRouteRow,
  onError?: (message: string) => void,
): void {
  const direct = adminUserEditPath(row);
  if (direct) {
    void router.navigate(direct);
    return;
  }
  resolveAdminUserUuid$(userApi, row)
    .pipe(take(1))
    .subscribe({
      next: (uuid) => {
        if (uuid) {
          void router.navigate(['/manage-admin-users', uuid, 'edit']);
          return;
        }
        onError?.('Could not open edit user. Refresh the list and try again.');
      },
      error: () => {
        onError?.('Could not open edit user. Refresh the list and try again.');
      },
    });
}
